/* ==========================================================================
   KCBRAND merkpagina

   Twee kleine dingen, meer niet: de actieve staat van de ankernavigatie en
   het wisselen tussen de tabbladen bij uitgelichte producten.

   Alle inhoud wordt server-side gerenderd. Zonder JavaScript blijven de
   ankerlinks gewoon werken en staan alle tabbladen onder elkaar zichtbaar,
   dus dit bestand is puur verfraaiing.
   ========================================================================== */

(function () {
  'use strict';

  /* ---------- Ankernavigatie ---------- */

  function initAnchors(root) {
    var nav = root.querySelector('[data-kcbrand-anchors]');
    if (!nav || nav.dataset.kcbrandReady === '1') return;
    nav.dataset.kcbrandReady = '1';

    var links = Array.prototype.slice.call(nav.querySelectorAll('a[href^="#"]'));
    if (!links.length) return;

    /* De sticky balk staat zelf boven de sectie waar je naartoe springt, dus
       zonder deze offset verdwijnt de sectiekop eronder. */
    function applyScrollOffset() {
      var h = nav.offsetHeight || 0;
      document.documentElement.style.setProperty('--kcbrand-scroll-offset', h + 8 + 'px');
    }
    applyScrollOffset();
    window.addEventListener('resize', applyScrollOffset);

    /* Een link naar een sectie die er niet staat, bijvoorbeeld omdat dit merk
       geen ranges heeft, verdwijnt. Liever een kortere balk dan een link die
       nergens heen springt. */
    var targets = [];
    links.forEach(function (link) {
      var id = link.getAttribute('href').slice(1);
      var el = id ? document.getElementById(id) : null;
      if (el) {
        targets.push({ link: link, el: el });
      } else {
        var li = link.closest('li');
        (li || link).hidden = true;
      }
    });

    if (!targets.length || !('IntersectionObserver' in window)) return;

    var visible = [];

    function paint() {
      /* De bovenste zichtbare sectie wint. Bij snel scrollen zijn er kort
         meerdere secties tegelijk in beeld en zou de laatste anders blijven
         hangen. */
      var top = null;
      visible.forEach(function (entry) {
        if (!top || entry.el.offsetTop < top.el.offsetTop) top = entry;
      });

      targets.forEach(function (t) {
        t.link.classList.toggle('is-active', !!top && t.el === top.el);
      });
    }

    var observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          var match = targets.filter(function (t) { return t.el === entry.target; })[0];
          if (!match) return;

          var idx = visible.indexOf(match);
          if (entry.isIntersecting && idx === -1) visible.push(match);
          if (!entry.isIntersecting && idx !== -1) visible.splice(idx, 1);
        });
        paint();
      },
      { rootMargin: '-25% 0px -60% 0px', threshold: 0 }
    );

    targets.forEach(function (t) { observer.observe(t.el); });
  }

  /* ---------- Tabbladen uitgelichte producten ---------- */

  function initTabs(root) {
    var groups = root.querySelectorAll('[data-kcbrand-tabs]');

    Array.prototype.forEach.call(groups, function (group) {
      if (group.dataset.kcbrandReady === '1') return;
      group.dataset.kcbrandReady = '1';

      var buttons = Array.prototype.slice.call(group.querySelectorAll('[data-kcbrand-tab]'));
      var panels = Array.prototype.slice.call(group.querySelectorAll('[data-kcbrand-panel]'));
      if (buttons.length < 2 || !panels.length) return;

      function select(name, focus) {
        buttons.forEach(function (btn) {
          var on = btn.dataset.kcbrandTab === name;
          btn.setAttribute('aria-selected', on ? 'true' : 'false');
          btn.setAttribute('tabindex', on ? '0' : '-1');
          if (on && focus) btn.focus();
        });
        panels.forEach(function (panel) {
          panel.hidden = panel.dataset.kcbrandPanel !== name;
        });
      }

      buttons.forEach(function (btn) {
        btn.addEventListener('click', function () {
          select(btn.dataset.kcbrandTab, false);
        });

        /* Pijltjestoetsen horen bij een tablist, anders is de groep met het
           toetsenbord niet te bedienen zoals gebruikers verwachten. */
        btn.addEventListener('keydown', function (e) {
          var i = buttons.indexOf(btn);
          var next = null;
          if (e.key === 'ArrowRight') next = buttons[(i + 1) % buttons.length];
          if (e.key === 'ArrowLeft') next = buttons[(i - 1 + buttons.length) % buttons.length];
          if (e.key === 'Home') next = buttons[0];
          if (e.key === 'End') next = buttons[buttons.length - 1];
          if (!next) return;
          e.preventDefault();
          select(next.dataset.kcbrandTab, true);
        });
      });

      select(buttons[0].dataset.kcbrandTab, false);
    });
  }

  /* ---------- Eigen sortering per tabblad ----------

     Een tabblad dat "best verkocht" of "nieuwste" wil, kan die volgorde niet
     uit Liquid krijgen: collection.products komt altijd in de volgorde van de
     collectie of van sort_by in de URL, en is bovendien afgekapt op vijftig.

     Dus vragen we dezelfde sectie nog een keer op, met sort_by erbij. Shopify
     bouwt de collectie dan in die volgorde. Wat er al staat is de serverkant
     met de volgorde van de pagina; die blijft gewoon staan als dit verzoek
     mislukt of als er geen JavaScript is.

     Liquid zet data-kcbrand-sort er alleen op als de pagina nog niet in de
     gevraagde volgorde staat, dus in het gunstige geval gebeurt hier niets.

     De respons kan twee vormen hebben en we moeten ze allebei aankunnen:

     - Stuurt Shopify de blokken niet mee, dan ziet de sectie zichzelf als los
       opgevraagd en geeft hij alleen een kale lijst terug, gemerkt met
       data-kcbrand-fetched.
     - Stuurt Shopify de blokken wél mee — en dat doet hij nu — dan komt de
       hele sectie terug, inclusief tabbladen. Dan moeten we het paneel pakken
       dat bij dít tabblad hoort.

     Op alleen de eerste vorm vertrouwen was precies wat er misging: zodra
     Shopify de blokken ging meesturen vond het script niets meer, haakte het
     stilletjes af en bleef de volgorde van de collectie staan onder de kop
     "Populair". */

  function initFeaturedSort(root) {
    var panels = root.querySelectorAll('[data-kcbrand-sort]');

    Array.prototype.forEach.call(panels, function (panel) {
      if (panel.dataset.kcbrandSorted === '1') return;
      panel.dataset.kcbrandSorted = '1';

      var host = panel.closest('.shopify-section');
      var id = host && host.id.replace(/^shopify-section-/, '');
      var current = panel.querySelector('.kcbrand-featured__grid');
      if (!id || !current || !window.fetch) return;

      /* Bewust opgebouwd uit het kale pad: filters of een sortering die de
         bezoeker koos horen hier niet in mee te liften. De taalprefix zit in
         het pad en blijft daardoor vanzelf staan. */
      var url = window.location.pathname
        + '?section_id=' + encodeURIComponent(id)
        + '&sort_by=' + encodeURIComponent(panel.dataset.kcbrandSort);

      fetch(url, { headers: { 'X-Requested-With': 'XMLHttpRequest' } })
        .then(function (res) { return res.ok ? res.text() : Promise.reject(res.status); })
        .then(function (html) {
          var doc = new DOMParser().parseFromString(html, 'text/html');

          var fresh = doc.querySelector('[data-kcbrand-fetched]');
          if (!fresh) {
            /* De hele sectie kwam terug. Het paneel van dit tabblad staat er
               dan ook in, met de kaarten in de gevraagde volgorde. */
            var zelfdePaneel = doc.querySelector('[data-kcbrand-panel="' + panel.dataset.kcbrandPanel + '"]');
            fresh = zelfdePaneel && zelfdePaneel.querySelector('.kcbrand-featured__grid');
          }

          if (!fresh || !fresh.children.length) return;
          current.innerHTML = fresh.innerHTML;

          /* Dezelfde twee signalen die facets.js afgeeft na een grid-swap:
             het eerste laat de productkaarten hun winkelwagenstatus
             bijwerken, het tweede is waar verlanglijst-apps op wachten. */
          window.dispatchEvent(new CustomEvent('collection:filtered'));
          panel.dispatchEvent(new CustomEvent('shopify:section:load', {
            bubbles: true,
            detail: { sectionId: id }
          }));
        })
        .catch(function () { /* de serverkant blijft staan */ });
    });
  }

  /* ---------- Doorlopende band ----------

     De band schuift met scrollLeft en niet met een transform-animatie. Dat is
     bewust omgedraaid: de bezoeker moet de rij kunnen vastpakken en zelf heen
     en weer kunnen schuiven, en dat botst met een animatie die de hele baan
     verplaatst. Nu doet de muis precies hetzelfde als een veeg op mobiel, en
     blijft de native touchscroll onaangeroerd.

     De lus is eindeloos doordat de rij een paar keer achter elkaar in de baan
     staat. Zodra de scrollpositie een hele rij verder is, gaat die rij er weer
     af. Op het scherm verandert er dan niets — dezelfde kaart blijft op
     dezelfde plek — maar er staat weer een volledige rij vóór en achter. Zo
     komt er nooit een begin of een einde in beeld, in beide richtingen.

     Hoe breed een kaart is komt uit de opmaak en niet uit dit script: in een
     baan van max-content valt er niets in procenten uit te rekenen, dus wordt
     de gemeten breedte er als pixelmaat op teruggezet. Lukt dat meten niet,
     dan blijft het een gewone rij die je zelf opzij veegt. */

  function initMarquee(root) {
    var els = root.querySelectorAll('[data-kcbrand-marquee]');

    Array.prototype.forEach.call(els, function (el) {
      if (el.dataset.kcbrandReady === '1') return;
      el.dataset.kcbrandReady = '1';

      var track = el.querySelector('.kcbrand-marquee__track');
      var list = track && track.querySelector('ul');
      if (!track || !list || !list.children.length) return;

      var speed = parseFloat(el.dataset.kcbrandSpeed) || 25;

      /* Bewegen dat je niet kunt stoppen is voor sommige mensen niet
         hinderlijk maar onbruikbaar. Deze query kan tijdens het bezoek
         omslaan, dus we lezen hem per beeldje in plaats van één keer. */
      var motion = window.matchMedia ? window.matchMedia('(prefers-reduced-motion: reduce)') : null;

      var unit = 0;      /* breedte van één rij, inclusief de ruimte erachter */
      var applied = -1;  /* containerbreedte waarop de laatste meting sloeg */
      var rest = 0;      /* deel van een pixel dat nog niet verschoven is */
      var frame = 0;
      var stamp = 0;
      var seen = true;
      var hover = false;
      var touching = false;
      var idle = null;
      var dragging = false;
      var dragX = 0;
      var dragFrom = 0;
      var dragged = 0;

      /* De kopieën komen van hier, niet uit de sjabloon: hoeveel er nodig zijn
         hangt af van de gemeten breedte. Zonder script blijft de kopie in de
         sjabloon verborgen en is het een gewone rij. */
      function strip() {
        while (list.nextElementSibling) track.removeChild(list.nextElementSibling);
      }

      /* De kopieën moeten wél aanklikbaar zijn: je kijkt er het grootste deel
         van de tijd naar. Ze mogen alleen niet nóg een keer in de tabvolgorde
         en bij de voorlezer langskomen. Daarom aria-hidden met tabindex -1 en
         geen inert: inert maakt ook het klikken dood. */
      function add() {
        var copy = list.cloneNode(true);
        copy.setAttribute('aria-hidden', 'true');

        var reachable = copy.querySelectorAll('a, button, input, select, textarea, [tabindex]');
        for (var i = 0; i < reachable.length; i++) {
          reachable[i].setAttribute('tabindex', '-1');
        }

        track.appendChild(copy);
      }

      function build() {
        /* Meten gebeurt in stilstand. Zodra de band loopt staan de kaarten op
           een pixelbreedte die dit script er zelf op heeft gezet, en dan meet
           je je eigen vorige uitkomst terug in plaats van wat de opmaak
           bedoelt. Op deze manier hoeft hier ook geen enkele maat uit de
           stylesheet herhaald te worden: hoe breed een kaart is en hoeveel er
           naast elkaar passen blijft één plek. */
        el.classList.remove('is-running');
        strip();
        unit = 0;

        var avail = el.clientWidth;
        var card = list.children[0].getBoundingClientRect().width;
        if (!avail || !card) return;

        el.style.setProperty('--kcbrand-marquee-item-w', card + 'px');
        el.classList.add('is-running');

        /* De maat van één ronde wordt opgemeten en niet nagerekend. Aantal keer
           kaartbreedte plus tussenruimte zit er zomaar een halve pixel naast,
           en die fout stapelt zich elke ronde op tot er een gat valt. */
        var gap = parseFloat(getComputedStyle(track).columnGap) || 0;
        unit = list.getBoundingClientRect().width + gap;
        if (unit <= 1) {
          el.classList.remove('is-running');
          unit = 0;
          return;
        }

        /* Er moet altijd een hele rij vóór en achter het zichtbare deel staan,
           anders is er één kant op niets om naartoe te schuiven. */
        var copies = Math.ceil(avail / unit) + 2;
        var i;
        for (i = 1; i < copies; i++) add();

        /* Narekenen alleen is te krap: padding en afronding kunnen net te
           weinig baan overlaten om twee hele rijen ver te kunnen schuiven, en
           dan loopt de lus vast tegen het einde van de scrollbalk. */
        for (i = 0; i < 4 && el.scrollWidth - el.clientWidth < unit * 2; i++) add();

        el.scrollLeft = unit;
        if (el.scrollLeft < unit - 1) {
          /* De baan blijkt toch te kort. Dan liever een gewone rij dan een lus
             die tegen het einde van de scrollbalk aan blijft duwen. */
          el.classList.remove('is-running');
          strip();
          unit = 0;
          return;
        }

        applied = avail;
      }

      /* De sprong die de lus dichtmaakt. Hij verzet alleen de scrollpositie:
         op het scherm staat daarna precies dezelfde kaart op dezelfde plek. */
      function wrap() {
        if (!unit) return;

        var at = el.scrollLeft;
        var moved = 0;

        while (at >= unit * 2) { at -= unit; moved -= unit; }
        while (at < unit - 1) { at += unit; moved += unit; }
        if (!moved) return;

        el.scrollLeft = at;
        dragFrom += moved;
      }

      function running() {
        if (!seen || !unit) return false;
        if (dragging || touching || hover) return false;
        return !(motion && motion.matches);
      }

      function tick(now) {
        frame = requestAnimationFrame(tick);

        var step = stamp ? now - stamp : 0;
        stamp = now;

        if (step > 0 && running()) {
          /* Een tabblad dat op de achtergrond stond levert anders één grote
             sprong op zodra het weer vooraan komt. */
          rest += speed * Math.min(step, 64) / 1000;
          var whole = Math.floor(rest);
          if (whole >= 1) {
            rest -= whole;
            el.scrollLeft = el.scrollLeft + whole;
          }
        }

        wrap();
      }

      function play() {
        if (frame) return;
        stamp = 0;
        frame = requestAnimationFrame(tick);
      }

      function halt() {
        if (!frame) return;
        cancelAnimationFrame(frame);
        frame = 0;
      }

      /* Stilzetten zodra iemand wil lezen of klikken. */
      el.addEventListener('mouseenter', function () { hover = true; });
      el.addEventListener('mouseleave', function () { hover = false; });
      el.addEventListener('focusin', function () { hover = true; });
      el.addEventListener('focusout', function () { hover = false; });

      /* Op touch scrollt de browser zelf; daar hoeft alleen het vanzelf
         doorschuiven uit. Een veeg loopt na het loslaten nog even uit, dus de
         band wacht dat af in plaats van er meteen tegenin te duwen. */
      function settle() {
        clearTimeout(idle);
        idle = setTimeout(function () { touching = false; }, 900);
      }

      el.addEventListener('touchstart', function () {
        clearTimeout(idle);
        touching = true;
      }, { passive: true });
      el.addEventListener('touchend', settle, { passive: true });
      el.addEventListener('touchcancel', settle, { passive: true });

      /* Slepen met de muis. Touch blijft hier expres buiten: dat doet de
         browser zelf beter, inclusief het uitlopen van een veeg. */
      el.addEventListener('pointerdown', function (e) {
        if (e.pointerType === 'touch' || e.button !== 0 || !unit) return;
        dragging = true;
        dragged = 0;
        dragX = e.clientX;
        dragFrom = el.scrollLeft;
        el.classList.add('is-dragging');
      });

      el.addEventListener('pointermove', function (e) {
        if (!dragging) return;

        var by = e.clientX - dragX;
        if (Math.abs(by) > dragged) dragged = Math.abs(by);

        /* Pas echt vastpakken zodra het slepen wordt, zodat een klik op een
           kaart een klik blijft. Daarna blijven de bewegingen binnenkomen, ook
           als de muis de band verlaat. */
        if (dragged > 3 && el.setPointerCapture && !el.hasPointerCapture(e.pointerId)) {
          try { el.setPointerCapture(e.pointerId); } catch (err) { /* aanwijzer is al weg */ }
        }

        e.preventDefault();
        el.scrollLeft = dragFrom - by;
        wrap();
      });

      function drop(e) {
        if (!dragging) return;
        dragging = false;
        el.classList.remove('is-dragging');

        if (el.hasPointerCapture && el.hasPointerCapture(e.pointerId)) {
          el.releasePointerCapture(e.pointerId);
        }

        /* Een sleep eindigt boven een kaart, en die kaart is een link. Zonder
           dit opent de browser het merk waar je toevallig losliet. */
        if (dragged > 4) {
          var block = function (ev) {
            ev.preventDefault();
            ev.stopPropagation();
          };
          el.addEventListener('click', block, true);
          setTimeout(function () { el.removeEventListener('click', block, true); }, 0);
        }
      }

      el.addEventListener('pointerup', drop);
      el.addEventListener('pointercancel', drop);

      /* Een afbeelding in een link wil de browser zelf gaan verslepen. */
      el.addEventListener('dragstart', function (e) {
        if (dragging) e.preventDefault();
      });

      build();

      /* Buiten beeld hoeft er niets te bewegen. */
      if (window.IntersectionObserver) {
        new IntersectionObserver(function (entries) {
          seen = entries[entries.length - 1].isIntersecting;
          if (seen) play();
          else halt();
        }, { rootMargin: '200px 0px' }).observe(el);
      } else {
        play();
      }

      /* Alleen opnieuw meten als de band echt een andere breedte krijgt. Een
         verschijnende scrollbalk, een adresbalk die op mobiel wegschuift of
         een paneel in de theme editor vuurt anders een hermeting af, en elke
         hermeting zet de band terug op het begin van een rij. */
      function onResize() {
        if (!el.isConnected) return;
        var now = el.clientWidth;
        if (unit && applied >= 0 && Math.abs(now - applied) < 1) return;
        build();
      }

      if (window.ResizeObserver) {
        new ResizeObserver(onResize).observe(el);
      } else {
        var timer;
        window.addEventListener('resize', function () {
          clearTimeout(timer);
          timer = setTimeout(onResize, 150);
        });
      }
    });
  }

  /* ---------- Zoeken binnen het merk ----------

     Rapid Search koppelt zich aan elk Shopify-zoekveld en kan dat niet tot
     één leverancier beperken. Dit veld is daarom expres geen zoekformulier:
     de vraag gaat naar Shopify's /search/suggest.json met vendor:"merk"
     ervoor. Die syntax eert de suggest-API wél — getest: acht resultaten,
     allemaal van het gevraagde merk. */

  function initSearch(root) {
    var boxes = root.querySelectorAll('[data-kcbrand-search]');

    Array.prototype.forEach.call(boxes, function (box) {
      if (box.dataset.kcbrandReady === '1') return;
      box.dataset.kcbrandReady = '1';

      var vendor = (box.dataset.kcbrandVendor || '').trim();
      var endpoint = box.dataset.kcbrandSuggest || '/search/suggest.json';
      var field = box.querySelector('.kcbrand-search__input');
      var panel = box.querySelector('.kcbrand-search__panel');
      var go = box.querySelector('[data-kcbrand-search-go]');
      if (!vendor || !field || !panel) return;

      var emptyText = box.dataset.kcbrandEmpty || '';
      var noneText = box.dataset.kcbrandNone || '';
      var prefix = 'vendor:"' + vendor + '"';
      var vendorNorm = vendor.toLowerCase();
      var timer;
      var req = 0;
      var items = [];
      var active = -1;

      function escapeHtml(value) {
        return String(value == null ? '' : value)
          .replace(/&/g, '&amp;')
          .replace(/</g, '&lt;')
          .replace(/>/g, '&gt;')
          .replace(/"/g, '&quot;');
      }

      function money(value) {
        if (value == null || value === '') return '';
        return '€' + String(value).replace('.', ',');
      }

      function imageUrl(product) {
        var img = product.featured_image || product.image;
        if (!img) return '';
        var src = typeof img === 'string' ? img : img.url;
        if (!src) return '';
        if (src.indexOf('//') === 0) src = 'https:' + src;
        return src.replace(/(\.[a-z]+)(\?|$)/i, '_120x$1$2');
      }

      function close() {
        panel.hidden = true;
        panel.innerHTML = '';
        field.setAttribute('aria-expanded', 'false');
        items = [];
        active = -1;
      }

      function paintActive() {
        var rows = panel.querySelectorAll('[role="option"]');
        Array.prototype.forEach.call(rows, function (row, i) {
          var on = i === active;
          row.classList.toggle('is-active', on);
          row.setAttribute('aria-selected', on ? 'true' : 'false');
        });
        if (rows[active]) field.setAttribute('aria-activedescendant', rows[active].id);
        else field.removeAttribute('aria-activedescendant');
      }

      function showMessage(text) {
        panel.innerHTML = '<p class="kcbrand-search__msg">' + text + '</p>';
        panel.hidden = false;
        field.setAttribute('aria-expanded', 'true');
        items = [];
        active = -1;
      }

      function render(products) {
        items = products;
        active = products.length ? 0 : -1;

        if (!products.length) {
          showMessage(noneText);
          return;
        }

        var html = products.map(function (p, i) {
          var src = imageUrl(p);
          var img = src
            ? '<img class="kcbrand-search__thumb" src="' + src + '" alt="" width="48" height="48" loading="lazy">'
            : '<span class="kcbrand-search__thumb" aria-hidden="true"></span>';
          var price = money(p.price_min || p.price);
          return (
            '<a class="kcbrand-search__hit" role="option" id="kcbrand-hit-' + i + '" href="' + p.url + '"' +
            (i === 0 ? ' aria-selected="true"' : ' aria-selected="false"') + '>' +
              img +
              '<span class="kcbrand-search__copy">' +
                '<span class="kcbrand-search__title">' + escapeHtml(p.title) + '</span>' +
                (p.type ? '<span class="kcbrand-search__type">' + escapeHtml(p.type) + '</span>' : '') +
              '</span>' +
              (price ? '<span class="kcbrand-search__price">' + price + '</span>' : '') +
            '</a>'
          );
        }).join('');

        panel.innerHTML = html;
        panel.hidden = false;
        field.setAttribute('aria-expanded', 'true');
        paintActive();
      }

      function query() {
        var term = field.value.trim();
        if (term.length < 2) {
          if (term.length === 1 && emptyText) showMessage(emptyText);
          else close();
          return;
        }

        var id = ++req;
        var url = endpoint +
          '?q=' + encodeURIComponent(prefix + ' ' + term) +
          '&resources[type]=product' +
          '&resources[limit]=8' +
          '&resources[options][unavailable_products]=last';

        fetch(url, { headers: { Accept: 'application/json' } })
          .then(function (res) { return res.json(); })
          .then(function (data) {
            if (id !== req) return;
            var list = (((data.resources || {}).results || {}).products) || [];
            list = list.filter(function (p) {
              return !p.vendor || p.vendor.toLowerCase() === vendorNorm;
            });
            render(list);
          })
          .catch(function () {
            if (id !== req) return;
            showMessage(noneText);
          });
      }

      function schedule() {
        clearTimeout(timer);
        timer = setTimeout(query, 180);
      }

      function openActive() {
        if (active < 0 || !items[active] || !items[active].url) return;
        window.location.href = items[active].url;
      }

      field.addEventListener('input', schedule);
      field.addEventListener('focus', function () {
        if (field.value.trim().length >= 2 && items.length) {
          panel.hidden = false;
          field.setAttribute('aria-expanded', 'true');
        } else if (field.value.trim().length >= 2) {
          query();
        }
      });

      field.addEventListener('keydown', function (e) {
        if (e.key === 'Escape') {
          close();
          return;
        }
        if (e.key === 'ArrowDown') {
          e.preventDefault();
          if (!items.length) { query(); return; }
          active = (active + 1) % items.length;
          paintActive();
          return;
        }
        if (e.key === 'ArrowUp') {
          e.preventDefault();
          if (!items.length) return;
          active = (active - 1 + items.length) % items.length;
          paintActive();
          return;
        }
        if (e.key === 'Enter') {
          e.preventDefault();
          if (items[active]) openActive();
          else query();
        }
      });

      if (go) go.addEventListener('click', function () { query(); field.focus(); });

      document.addEventListener('click', function (e) {
        if (!box.contains(e.target)) close();
      });
    });
  }

  function init(root) {
    var scope = root || document;
    initAnchors(scope === document ? document : scope);
    initTabs(scope);
    initFeaturedSort(scope);
    initMarquee(scope);
    initSearch(scope);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { init(document); });
  } else {
    init(document);
  }

  /* De theme editor vervangt losse secties zonder de pagina te herladen. */
  document.addEventListener('shopify:section:load', function (e) {
    init(e.target || document);
  });
})();
